import { useEffect, useState } from 'react';
import { Link as RouterLink, useOutletContext } from 'react-router-dom';
import { api, conferenceLabel, durationsOf, fmtMinutes, type Connection, type Link, type Me } from '../api';
import { ErrorBox, Loading } from '../components/bk';

export function Links() {
  const me = useOutletContext<Me | null>();
  const [links, setLinks] = useState<Link[] | null>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');

  const [broken, setBroken] = useState<Connection[]>([]);

  useEffect(() => {
    api.get<Link[]>('/api/links').then(setLinks).catch((e) => setError(e.message));
    api
      .get<Connection[]>('/api/connections')
      .then((cs) => setBroken(cs.filter((c) => c.status === 'error')))
      .catch(() => undefined);
  }, []);

  const url = (slug: string) => `${location.origin}/b/${slug}`;
  const copy = async (slug: string) => {
    try {
      await navigator.clipboard.writeText(url(slug));
      setCopied(slug);
      setTimeout(() => setCopied(''), 1500);
    } catch {
      prompt('Copy this link', url(slug));
    }
  };
  const share = (l: Link) =>
    navigator.share ? navigator.share({ title: l.title, url: url(l.slug) }).catch(() => undefined) : copy(l.slug);

  return (
    <>
      <div className="pagehead">
        <div>
          <h1>Booking links</h1>
          <p className="muted">Share a link; people pick a time when every host is free.</p>
        </div>
        <RouterLink to="/links/new" className="btn primary">+ New link</RouterLink>
      </div>
      <ErrorBox error={error} />
      {broken.length > 0 && (
        <div className="alert" role="alert">
          <strong>Reconnect needed:</strong> {broken.map((c) => c.accountEmail).join(', ')}. Busy time from{' '}
          {broken.length === 1 ? 'this calendar' : 'these calendars'} isn't blocking your links until you{' '}
          <RouterLink to="/connections">reconnect on the Calendars page</RouterLink>.
        </div>
      )}
      {!links ? (
        <Loading />
      ) : !links.length ? (
        <div className="card empty">
          <p>No booking links yet.</p>
          <p className="small">Start by connecting a calendar, then create your first link.</p>
          <div className="row" style={{ justifyContent: 'center' }}>
            <RouterLink to="/connections" className="btn">Connect calendars</RouterLink>
            <RouterLink to="/links/new" className="btn primary">Create link</RouterLink>
          </div>
        </div>
      ) : (
        <div className="grid">
          {links.map((l) => (
            <article key={l.slug} className="card" style={{ marginBottom: 0 }}>
              <div className="row between">
                <h3 style={{ margin: 0 }}>{l.title}</h3>
                {l.pausedByPlan ? <span className="badge err" title="Your company's plan allows fewer links">Paused by plan</span> : !l.active && <span className="badge">Off</span>}
              </div>
              <p className="muted small" style={{ margin: '4px 0 8px' }}>
                {durationsOf(l).map(fmtMinutes).join(' / ')} · {conferenceLabel[l.conferenceType]}
                {l.hosts.length > 1 && ` · ${l.hosts.length} hosts`}
              </p>
              {l.hosts.length > 1 && <p className="small">{l.hosts.map((h) => h.name).join(', ')}</p>}
              <p className="small" style={{ wordBreak: 'break-all' }}>
                <a href={url(l.slug)} target="_blank" rel="noreferrer">/b/{l.slug}</a>
              </p>
              <div className="row">
                <button onClick={() => share(l)}>{copied === l.slug ? 'Copied!' : 'Share'}</button>
                {me && l.ownerSub === me.sub ? (
                  <RouterLink className="btn" to={`/links/${l.slug}`}>Edit</RouterLink>
                ) : (
                  <span className="badge">Co-host</span>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </>
  );
}
